/**
 * @ken/module-payments — frontend entry point.
 *
 * Mirrors @ken/module-invoicing + @ken/module-suppliers shape.
 *
 * Sprint 2 P1: deposits fold-in adds /payments/deposits route +
 * DepositListPage / DepositDetailSheet exports.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { isRecordId, RecordNotFound } from "@ken/ui";
import { createRoute, notFound, redirect } from "@tanstack/react-router";

import type { PaymentStorySearch } from "./routes/story/payment-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadPaymentStoryTabs = () => import("./routes/story/payment-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const PaymentListPage = lazyScreen(
  () => import("./routes/payment-list"),
  "PaymentListPage",
);
const DepositListPage = lazyScreen(
  () => import("./routes/deposit-list"),
  "DepositListPage",
);
const PaymentStoryDetail = lazyScreen(
  () => import("./routes/story/payment-story-detail"),
  "PaymentStoryDetail",
);
const PaymentActivityRoute = lazyScreen(
  loadPaymentStoryTabs,
  "PaymentActivityRoute",
);
const PaymentAppliedToRoute = lazyScreen(
  loadPaymentStoryTabs,
  "PaymentAppliedToRoute",
);
const PaymentNotesRoute = lazyScreen(
  loadPaymentStoryTabs,
  "PaymentNotesRoute",
);
const PaymentOverviewRoute = lazyScreen(
  loadPaymentStoryTabs,
  "PaymentOverviewRoute",
);
const PaymentRelatedRoute = lazyScreen(
  loadPaymentStoryTabs,
  "PaymentRelatedRoute",
);
export { DepositDetailSheet } from "./components/deposit-detail-sheet";

// UI-06: payments has no create endpoint (no POST /api/modules/payments), so
// this takes the "create surface out of scope" outcome. Recording a payment is
// a governed action taken from an invoice, not a blank form reached by typing
// a URL — see the Record Payment flow on the invoice story page.

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "payments",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof; the manual writeQueryParams is retired.
    validateSearch: (raw: Record<string, unknown>): PaymentListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: PaymentListPage,
  });

  const depositsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "payments/deposits",
    component: DepositListPage,
  });

  // UI-06: a STATIC route for the literal `new`, so the id route below can
  // never match it and fire a detail read for a record called "new".
  const literalNewRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "payments/new",
    component: RecordNotFound,
  });

  // ── Story-page detail (ADR-0081): parent payments/$id shell + routed tab
  //    children + an index redirect to …/overview. ─────────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "payments/$id",
    // UI-06's id-shape guard: a segment that could never be a record id stops
    // here, BEFORE any detail or graph request is issued.
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { id?: string }).id)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    validateSearch: (raw: Record<string, unknown>): PaymentStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
    }),
    component: PaymentStoryDetail,
  });

  // Index redirect payments/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/payments/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: PaymentOverviewRoute,
  });
  const appliedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "applied",
    component: PaymentAppliedToRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: PaymentRelatedRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: PaymentNotesRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: PaymentActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      appliedRoute,
      relatedRoute,
      notesRoute,
      activityRoute,
    ],
  );

  // Static before parameterised.
  return [listRoute, depositsRoute, literalNewRoute, detailRoute];
};

/** List search params (filter/search/highlight) — URL-borne for restore. */
export interface PaymentListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}
